import React, { useRef } from "react";
import { Loader2, Languages, X } from "lucide-react";
import { useTextSelectionTranslate } from "../hooks/useTextSelectionTranslate";

/**
 * Floating EN→AR translation popover that appears whenever the user highlights
 * English text anywhere in the app. All selection + API logic lives in the
 * `useTextSelectionTranslate` hook so this component stays presentational.
 */
export default function TranslatePopover() {
  const popRef = useRef(null);
  const { state, close } = useTextSelectionTranslate(popRef);

  if (!state.open) return null;

  return (
    <div
      ref={popRef}
      data-no-translate="true"
      data-testid="translate-popover"
      style={{ position: "absolute", top: state.y, left: state.x, width: 340, zIndex: 9999 }}
      className="rounded-xl border border-[#e5d9c9] bg-[#fdfaf4] shadow-[0_10px_30px_rgba(74,103,92,0.18)] backdrop-blur-sm overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 bg-[#4A675C] text-[#fdfaf4]">
        <div className="flex items-center gap-2 text-xs font-medium tracking-wide uppercase">
          <Languages size={14} />
          <span>English → العربية</span>
        </div>
        <button
          type="button"
          onClick={close}
          data-testid="translate-popover-close"
          aria-label="Close translation"
          className="p-1 rounded hover:bg-white/10 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-[#efe5d3] text-[11px] text-[#6E7A75] leading-snug line-clamp-2">
        {state.source}
      </div>

      <div className="px-3 py-3 min-h-[56px]">
        {state.loading && (
          <div className="flex items-center gap-2 text-[#6E7A75] text-sm">
            <Loader2 className="animate-spin" size={14} />
            <span>Translating…</span>
          </div>
        )}
        {!state.loading && state.error && (
          <div className="text-sm text-[#B34A3A]" data-testid="translate-popover-error">
            {state.error}
          </div>
        )}
        {!state.loading && !state.error && state.ar && (
          <p
            dir="rtl"
            lang="ar"
            data-testid="translate-popover-arabic"
            className="text-[#2e3b36] leading-relaxed text-right"
            style={{ fontSize: "1.02rem" }}
          >
            {state.ar}
          </p>
        )}
      </div>
    </div>
  );
}
